import { create } from "zustand";

import { PageName } from "analytics/events";
import { SortOrder } from "utils/item";

import { PrivateRestockingList } from "../models/privateList/types";

export type AppDisplayMode = "browser" | "standalone" | "fullscreen";

export type RestockingServiceStore = {
  privateRestockingListData: PrivateRestockingList | null;
  sortOrder: SortOrder;
  sortBy: "shelf" | "priority";
  isLoadingPrivateRestockingList: boolean;
  itemInRestockingProcess: string | null;
  totalRestockingItemCount: number;
  isNavigatingToStockCorrection: boolean;
  productSku: string | null;
  activeItemId: string | null;
  selectedItemsBeingRemoved: string[];
  privateRestockingListRefreshNeeded: boolean;
  pageName: PageName | null;
  categoryFilters: string[];
  shelfFilters: string[];
  priorityFilters: string[];
};

type RestockingServiceStoreWithMethods = RestockingServiceStore & {
  setPrivateRestockingListData(privateRestockingList: PrivateRestockingList | null): void;
  toggleSortOrder(): void;
  setIsLoadingPrivateRestockingList(v: boolean): void;
  setItemInRestockingProcess(sku: string | null): void;
  getCurrentPrivateRestockingListId(): string | null;
  getShelfNumber(sku: string): string;
  setUseRestockingServiceStore(restockingServiceStore: Partial<RestockingServiceStore>): void;
  updateSelectedItemsForRemoval(skus: string[]): void;
  updateCategoryFilters(categoryFilters: string[]): void;
  updateShelfFilters(shelfFilters: string[]): void;
  updatePriorityFilters(priorityFilters: string[]): void;
  setPrivateRestockingListRefreshNeeded(privateRestockingListRefreshNeeded: boolean): void;
  setSortOrder(sortOrder: SortOrder): void;
  setSortBy(sortBy: "shelf" | "priority"): void;
};

const initialState: RestockingServiceStore = {
  privateRestockingListData: null,
  sortOrder: "asc",
  sortBy: "priority",
  isLoadingPrivateRestockingList: false,
  itemInRestockingProcess: null,
  totalRestockingItemCount: 0,
  isNavigatingToStockCorrection: false,
  productSku: null,
  activeItemId: null,
  selectedItemsBeingRemoved: [],
  privateRestockingListRefreshNeeded: false,
  pageName: null,
  categoryFilters: [],
  shelfFilters: [],
  priorityFilters: [],
};

export const useRestockingServiceStore = create<RestockingServiceStoreWithMethods>()(
  (set, get) => ({
    ...initialState,
    setPrivateRestockingListData(privateRestockingListData) {
      set({
        privateRestockingListData,
        totalRestockingItemCount: privateRestockingListData?.restockingItems?.length ?? 0,
      });
    },
    setUseRestockingServiceStore(restockingServiceStore) {
      set(restockingServiceStore);
    },
    toggleSortOrder() {
      set({ sortOrder: get().sortOrder === "asc" ? "desc" : "asc" });
    },
    setSortOrder(sortOrder: SortOrder) {
      set({ sortOrder });
    },
    setSortBy(sortBy: "shelf" | "priority") {
      set({ sortBy });
    },
    setIsLoadingPrivateRestockingList(v: boolean) {
      set({ isLoadingPrivateRestockingList: v });
    },
    setItemInRestockingProcess(sku: string | null) {
      set({ itemInRestockingProcess: sku });
    },
    getCurrentPrivateRestockingListId() {
      return get().privateRestockingListData?.id || null;
    },
    getShelfNumber(sku: string) {
      return (
        get().privateRestockingListData?.restockingItems.find((item) => sku === item.sku)?.product
          ?.shelf ?? ""
      );
    },
    updateSelectedItemsForRemoval(skus: string[]) {
      set({ selectedItemsBeingRemoved: skus });
    },
    updateCategoryFilters(categoryFilters: string[]) {
      set({ categoryFilters });
    },
    updateShelfFilters(shelfFilters: string[]) {
      set({ shelfFilters });
    },
    updatePriorityFilters(priorityFilters: string[]) {
      set({ priorityFilters });
    },
    setPrivateRestockingListRefreshNeeded(privateRestockingListRefreshNeeded: boolean) {
      set({ privateRestockingListRefreshNeeded });
    },
  }),
);
